import { FC, MouseEvent, useState, useRef } from "react";
import styled, { css } from "styled-components";

import Button from "@/components/Button";
import { ReactComponent as ExportIcon } from "@/assets/icons/export.svg";
import useAlerts from "@/hooks/useAlerts";
import { download } from "@/utils/helpers";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import Text from "./Text";
import CsvExportModal, { ExportGroup } from "./CsvExportModal";

const Wrap = styled.div`
  position: relative;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 210px;
  padding: 4px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
`;

const DropdownButton = styled.button`
  height: 40px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  transition: 0.2s ease-out background-color;
  ${Text} {
    transition: 0.2s ease-out color;
  }
  &:hover {
    background: ${({ theme }) => theme.colors.grayscaleLight};
    ${Text} {
      color: ${({ theme }) => theme.colors.black};
    }
  }
`;

interface DropdownIconProps {
  $rotate: boolean;
}

const DropdownIcon = styled.svg<DropdownIconProps>`
  transition: 0.2s ease-out all;
  margin-left: 12px;
  ${({ $rotate }) =>
    $rotate &&
    css`
      transform: rotate(180deg);
    `}
`;

interface ExportModalOptions {
  primaryTitle: string;
  secondaryTitle: string;
  exportGroup: ExportGroup;
  onExport: (cols: string[]) => Promise<string>;
}

interface ExportOption {
  title: string;
  fetchCsv?: () => Promise<string>;
  fileName?: string;
  modalOptions?: ExportModalOptions;
}

interface Props {
  className?: string;
  fileName?: string;
  fetchCsv?: () => Promise<string>;
  title?: string;
  exportOptions?: ExportOption[];
}

const ExportButton: FC<Props> = ({
  className,
  fileName,
  fetchCsv,
  title = "Export as CSV",
  exportOptions,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const alerts = useAlerts();

  const [isExporting, setIsExporting] = useState(false);

  const [showDropdown, setShowDropdown] = useState(false);

  const [modalOptions, setModalOptions] = useState<ExportModalOptions | null>(
    null
  );

  useOnClickOutside(dropdownRef, () => setShowDropdown(false));

  const onExport = async (
    fileName = "export.csv",
    fetchCsv: () => Promise<string>
  ) => {
    try {
      const confirmed = await alerts.confirm(
        "Are you sure you want to export it as CSV"
      );

      if (!confirmed) {
        return;
      }

      setIsExporting(true);

      const csv = await fetchCsv();

      download(csv, fileName);

      setIsExporting(false);

      alerts.close();
    } catch (err) {
      alerts.error();
      setIsExporting(false);
    }
  };

  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!!exportOptions) {
      return setShowDropdown((prev) => !prev);
    }

    onExport(fileName, fetchCsv!);
  };

  return (
    <Wrap>
      <Button
        className={className}
        onClick={onClick}
        color="secondary"
        small
        loading={isExporting}
        disabled={isExporting}
      >
        <ExportIcon />
        {title}
        {!!exportOptions && (
          <DropdownIcon
            width="8"
            height="6"
            viewBox="0 0 8 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            $rotate={!showDropdown}
          >
            <path d="M4 0.5L8 5.5H0L4 0.5Z" fill="currentColor" />
          </DropdownIcon>
        )}
      </Button>
      {showDropdown && (
        <Dropdown ref={dropdownRef}>
          {exportOptions?.map((option, i) => (
            <DropdownButton
              key={i}
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                if (option.modalOptions) {
                  setModalOptions(option.modalOptions);
                } else {
                  onExport(option.fileName, option.fetchCsv!);
                }
                setShowDropdown(false);
              }}
            >
              <Text as="span" $type="captionSemibold" $color="gray">
                {option.title}
              </Text>
            </DropdownButton>
          ))}
        </Dropdown>
      )}
      {modalOptions && (
        <CsvExportModal
          {...modalOptions}
          fileName={fileName}
          onClose={() => setModalOptions(null)}
        />
      )}
    </Wrap>
  );
};

export default ExportButton;
